import React, { useEffect, useState, FC } from 'react'
import {
  connect,
  MapDispatchToPropsParam,
  MapStateToPropsParam
} from 'react-redux'
import { find } from 'lodash'
import { Alert } from 'antd'
import {
  Log,
  Carer,
  Home,
  Resident,
  LogCategory,
  LogCategoryItem,
  GoalListResponse
} from '@lmc/ts-common'
import { RouterProps } from '@lmc/common/types'
import { sizes } from '@lmc/common/constants'
import { Button, EmptyState, HeaderSection, NotFoundIcon } from '@lmc/ui-kit'
import { subject } from '@casl/ability'
import Operations from '../../../operations'
import { getJSON } from '../../../common/services/api'
import { Spinner } from '../../../components/generic'
import { withRouter } from '../../../withRouter'
import { history } from '../../../store'
import _LogEditForm from './components/LogEditForm'
import LogDetails from './components/LogDetails'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import useGoals from 'screens/Residents/screens/ResidentsGoalsScreen/components/useGoals/useGoals'
import IncidentLogScreen from './components/IncidentLogScreens/IncidentReportScreen'
import useIncidentReportByLog from 'hooks/useIncidentReport/useIncidentReportByLog'
import Selectors from 'selectors'
import { RoleAbilityProp, RoleAccessProp } from 'components/AccessControl'
import { commonPatterns } from '../../../utils/enhancedTestIds'

interface LogWithRevisions extends Log {
  logRevisions?: Log[]
  externalId?: string
}

interface StateProps {
  role: string
  loggedBy: Carer
  editedBy: Carer
  witnessedBy: Carer
  resident: Resident
  item: LogCategoryItem
  category: LogCategory
  features: Home
  log: any // LogWithRevisions | undefined
  hasAccess?: any
  testId?: string
}

interface DispatchProps {
  loadLog: () => Promise<void>
  loadPainChekData: (id: string) => Promise<void>
  updateListItem: (opts: any) => void
  router: RouterProps
}

// PainChekAssessment is in the latest version of TS-Common
interface IPainChekAssessment {
  uuid?: string
  patient?: string
  json_version?: string
  data?: {
    assessment?: {
      date?: string
      mode?: string
      pain_score?: number
      pain_level?: string
      is_editable?: boolean
      timing?: string
      comment?: string
      body_location?: string
    }
    device?: {
      manufacturer?: string
      model?: string
      os_type?: string
      os_version?: string
      app_version?: string
    }
    face?: {
      mode?: string
      initial_mode?: string
      camera?: string
      capture_seconds?: number
      frames?: number
      frame_errors?: number
      comment?: string
      score?: number
      descriptors?: {
        brow_lowering?: boolean
        cheek_raising?: boolean
        tightening_eyelids?: boolean
        wrinkling_nose?: boolean
        raising_upper_lip?: boolean
        pulling_corner_lip?: boolean
        mouth_stretch?: boolean
        parting_lips?: boolean
        closing_eyes?: boolean
      }
    }
    voice?: {
      voice_recording?: string
      comment?: string
      score?: number
      descriptors?: {
        noisy_sounds?: boolean
        requesting_help?: boolean
        groaning?: boolean
        moaning?: boolean
        crying?: boolean
        screaming?: boolean
        loudtalk?: boolean
        howling?: boolean
        sighing?: boolean
      }
    }
    movement?: {
      comment?: string
      score?: number
      descriptors?: {
        altered_random_movement?: boolean
        restlessness?: boolean
        freezing?: boolean
        guarding_touching?: boolean
        moving_away?: boolean
        abnormal_movement?: boolean
        pacing_wandering?: boolean
      }
    }
    behaviour?: {
      comment?: string
      score?: number
      descriptors?: {
        introvert?: boolean
        verbally_offensive?: boolean
        aggressive?: boolean
        fear_or_dislike?: boolean
        inappropriate_behaviour?: boolean
        confused?: boolean
        distressed?: boolean
      }
    }
    activity?: {
      comment?: string
      score?: number
      descriptors?: {
        resisting_care?: boolean
        prolonged_resting?: boolean
        altered_sleep?: boolean
        altered_routine?: boolean
      }
    }
    body?: {
      comment?: string
      score?: number
      descriptors?: {
        profuse_sweating?: boolean
        pale_flushed?: boolean
        feverish_cold?: boolean
        rapid_breathing?: boolean
        painful_injuries?: boolean
        painful_conditions?: boolean
      }
    }
  }
}

interface PainChekResponse {
  result: IPainChekAssessment
}

type Props = StateProps & DispatchProps

const LogEditForm = _LogEditForm as unknown as React.JSXElementConstructor<{
  log: LogWithRevisions
  onEditComplete: () => void
  logGoals?: GoalListResponse[]
  defaultGoals?: string[]
  testId?: string
}>

export const ViewLogScreen: FC<Props> = ({
  editedBy,
  witnessedBy,
  loggedBy,
  features,
  log,
  role,
  loadLog,
  resident,
  item,
  category,
  updateListItem,
  hasAccess,
  testId = 'log-screen'
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [painChekAssessment, setPainChekAssessment] =
    useState<IPainChekAssessment>()
  const { t } = useTranslation()

  const { incidentReport, isIncidentReportLoading } = useIncidentReportByLog({
    incidentLogId: log?.id,
    enabled: category?.itemId === 'incident'
  })

  useEffect(() => {
    loadLog().then(() => setIsLoading(false))
  }, [])

  const { goals: logGoals } = useGoals(resident?.id)

  useEffect(() => {
    if (log?.id && log.externalId) {
      getJSON({
        url: `/api/services/painchek/assessments/${log.externalId}`
      }).then((res: PainChekResponse) => {
        setPainChekAssessment(res.result)
      })
    }
  }, [log?.id])

  const hasRolePermission = hasAccess([
    {
      i: 'view',
      this: subject('Log', {
        item: incidentReport?.log.item?.id
      })
    }
  ])

  if (
    category?.itemId === 'incident' &&
    features.features?.pro?.enabled &&
    incidentReport !== undefined &&
    Object.keys(incidentReport).length > 0
  ) {
    return <IncidentLogScreen />
  }

  const goBack = () => history.back()

  const handleDeleteUndo = () =>
    updateListItem({
      listId: 'logs',
      successMsg: t('components.logEditForm.logRestored'),
      itemId: log.id,
      json: { deleted: false }
    })

  const renderDelete = () => (
    <Alert
      message={t('components.logEditForm.logRemoved')}
      type="info"
      data-testid={`${testId}-delete-alert`}
      action={
        <Button
          variant="default"
          onClick={handleDeleteUndo}
          data-testid={commonPatterns.button(testId, 'undo')}
        >
          {t('general.buttons.undo')}
        </Button>
      }
    />
  )

  const renderLogDetails = () => {
    const hasRolePermission = hasAccess([
      {
        i: 'update',
        this: subject('Log', {
          item: incidentReport?.log.item?.id
        })
      }
    ])

    const isPainchekItem = item?.itemId === 'painchek'

    return (
      <div
        style={styles.logDetailsContainer}
        data-testid={`${testId}-details-container`}
      >
        <HeaderSection
          backLink={true}
          title={log.title}
          onClick={goBack}
          iconColor={category?.color}
          iconContent={
            <img
              // @ts-ignore
              src={item?.icon || category?.icon || ''}
              alt="icon"
              style={styles.headerIcon}
            />
          }
          rightChildren={
            log.nonEditable ? undefined : (
              <Button
                disabled={log.deleted}
                onClick={() => setIsEditing(true)}
                variant="primary"
                // @ts-ignore
                disabledByPermission={!hasRolePermission || isPainchekItem}
                data-testid={commonPatterns.button(testId, 'edit')}
                {...(isPainchekItem
                  ? { permissionDisabledText: 'PainChek logs are read-only' }
                  : null)}
              >
                {t('general.buttons.edit')}
              </Button>
            )
          }
          testId={testId}
        />
        {log.deleted && renderDelete()}
        <LogDetails
          log={log}
          resident={resident}
          item={item}
          loggedBy={loggedBy}
          witnessedBy={witnessedBy}
          editedBy={editedBy}
          features={features}
          painChekAssessmentResult={painChekAssessment}
          testId={testId}
        />
      </div>
    )
  }

  if (!hasRolePermission)
    return (
      <EmptyState
        title="We cant find what you're looking for... Error 404"
        description="Don't worry, try refreshing the page and if the problem persists contact support."
        icon={<NotFoundIcon />}
      >
        <Link to="/">Go back home</Link>
      </EmptyState>
    )
  if (isLoading || isIncidentReportLoading || !log) return <Spinner />

  return (
    <div style={styles.wrapper} data-testid={testId}>
      {isEditing ? (
        <LogEditForm
          log={log}
          onEditComplete={() => setIsEditing(false)}
          logGoals={logGoals}
          defaultGoals={log.goals}
          testId={testId}
        />
      ) : (
        renderLogDetails()
      )}
    </div>
  )
}

const styles = {
  backLink: { padding: 0 },
  wrapper: {
    margin: 'auto',
    maxWidth: sizes.childScreen,
    padding: '20px 20px 50px'
  },
  headerIcon: { height: 20 },
  logDetailsContainer: { paddingBottom: 12 }
}

const mapStateToProps = (
  state: {
    global: {
      home: Home
      user: Carer
    }
    data: {
      logs: LogWithRevisions[]
      carers: Carer[]
      residents: Resident[]
      'log-categories': any[]
      'log-category-items': any[]
    }
  },
  props: Props
) => {
  const log = find(state.data.logs, { id: props.router.params.id })
  return {
    log,
    role: state.global.user.role,
    features: state.global.home,
    loggedBy: find(state.data.carers, { id: log?.loggedBy }),
    editedBy: find(state.data.carers, { id: log?.editedBy }),
    witnessedBy: find(state.data.carers, { id: log?.witnessedBy }),
    resident: find(state.data.residents, { id: log?.resident }),
    category: find(state.data['log-categories'], { id: log?.category }),
    item: find(state.data['log-category-items'], { id: log?.item }),
    hasAccess: (abilities: RoleAbilityProp) =>
      // @ts-ignore
      Selectors.checkAbilityAccess(state, { abilities })
  }
}

const mapDispatchToProps = (dispatch: any, props: Props) => ({
  updateListItem: (options: {
    list: string
    id: string
    body: { deleted: boolean }
  }) => dispatch(Operations.updateListItem(options)),
  loadLog: () =>
    Operations.loadListItem('logs', props.router.params.id, {
      withRevisions: true
    })(dispatch)
})

export default withRouter(
  connect<StateProps, DispatchProps, Props>(
    mapStateToProps as MapStateToPropsParam<StateProps, Props, unknown>,
    mapDispatchToProps as unknown as MapDispatchToPropsParam<
      DispatchProps,
      Props
    >
  )(ViewLogScreen)
)
